import charactersJson from "../../content/characters.json";
import enemiesJson from "../../content/enemies.json";
import level01Json from "../../content/level01.json";
import level02Json from "../../content/level02.json";
import level03Json from "../../content/level03.json";
import level04Json from "../../content/level04.json";
import tuningJson from "../../content/tuning.json";
import { MAX_SUPPORTED_PLAYERS } from "./difficulty";
import { isValidEnemyEntrance } from "./entrance";
import { isPropKind } from "./props";
import {
  DASH_STYLES,
  DIFFICULTY_TIERS,
  SPAWN_SPOT_STYLES,
  type CharacterDefinition,
  type DifficultyDefinition,
  type EnemyDefinition,
  type GameContent,
  type LevelDefinition,
  type LevelSpawnSpot,
  type TuningDefinition
} from "./types";

const characters = charactersJson as CharacterDefinition[];
const enemies = enemiesJson as EnemyDefinition[];
const levels = [level01Json, level02Json, level04Json, level03Json] as LevelDefinition[];
const tuning = tuningJson as TuningDefinition;

function validateContent(content: GameContent): GameContent {
  const characterIds = new Set(content.characters.map((character) => character.id));
  const enemyIds = new Set(content.enemies.map((enemy) => enemy.id));

  if (characterIds.size !== content.characters.length || enemyIds.size !== content.enemies.length) {
    throw new Error("Character and enemy IDs must be unique.");
  }

  if (content.levels.length === 0) {
    throw new Error("At least one level must be configured.");
  }

  for (const level of content.levels) {
    for (const prop of level.props ?? []) {
      if (!isPropKind(prop.kind)) {
        throw new Error(`Level ${level.id} has unknown floor prop ${prop.kind}.`);
      }
      if (prop.y < level.groundTop || prop.y > level.groundBottom) {
        throw new Error(`Level ${level.id} prop ${prop.kind} is off the walkable floor.`);
      }
    }
    const spots = new Map<string, LevelSpawnSpot>();
    for (const spot of level.spawnSpots ?? []) {
      if (spots.has(spot.id)) {
        throw new Error(`Level ${level.id} has two spawn spots named ${spot.id}.`);
      }
      spots.set(spot.id, spot);
      if (!SPAWN_SPOT_STYLES.includes(spot.style)) {
        throw new Error(`Level ${level.id} spawn spot ${spot.id} has unknown style ${spot.style}.`);
      }
      if (!(spot.width > 0) || !(spot.height > 0) || spot.x < 0 || spot.x > level.width) {
        throw new Error(`Level ${level.id} spawn spot ${spot.id} is outside the stage.`);
      }
    }
    for (const wave of level.waves) {
      for (const spawn of wave.spawns) {
        if (!enemyIds.has(spawn.enemyId)) {
          throw new Error(`Wave ${level.id}/${wave.id} references missing enemy ${spawn.enemyId}.`);
        }
        if (!isValidEnemyEntrance(spawn.entrance)) {
          throw new Error(`Wave ${level.id}/${wave.id} has an invalid entrance for ${spawn.enemyId}.`);
        }
        if (spawn.entrance?.type === "climb" && level.ledgeY === undefined) {
          throw new Error(`Wave ${level.id}/${wave.id} climbs a ledge, but ${level.id} has no ledgeY.`);
        }
        for (const id of spawn.entrance?.from ?? []) {
          const spot = spots.get(id);
          if (!spot) {
            throw new Error(`Wave ${level.id}/${wave.id} sends ${spawn.enemyId} through missing spot ${id}.`);
          }
          if ((spot.style === "cybertruck") !== (spawn.entrance?.type === "vehicle")) {
            throw new Error(`Wave ${level.id}/${wave.id} must arrive by vehicle exactly at truck spots.`);
          }
        }
      }
    }
  }

  for (const enemy of content.enemies) {
    if (!isValidEnemyEntrance(enemy.entrance) || enemy.entrance?.from) {
      throw new Error(`Enemy ${enemy.id} has an invalid entrance.`);
    }
  }

  for (const character of content.characters) {
    validateDash(character);
    validatePortrait(character);
  }
  if (!(content.tuning.dashTapWindow > 0)) {
    throw new Error("dashTapWindow must be greater than zero.");
  }

  if (content.tuning.maxPlayers !== MAX_SUPPORTED_PLAYERS) {
    throw new Error(`maxPlayers must be exactly ${MAX_SUPPORTED_PLAYERS}.`);
  }

  validateDifficulty(content.tuning.difficulty);

  return content;
}

function validateDash({ id, dash }: CharacterDefinition): void {
  if (!dash) return;
  if (dash.style !== undefined && !DASH_STYLES.includes(dash.style)) {
    throw new Error(`Character ${id} has unknown dash style ${dash.style}.`);
  }
  if (!(dash.speed > 0) || !(dash.duration > 0)) {
    throw new Error(`Character ${id} dash speed and duration must be greater than zero.`);
  }
  if (!(dash.cooldown >= 0)) {
    throw new Error(`Character ${id} dash cooldown cannot be negative.`);
  }
  const invulnerable = dash.invulnerable ?? 0;
  if (!(invulnerable >= 0) || invulnerable > dash.duration) {
    throw new Error(`Character ${id} dash invulnerability must fit inside the dash.`);
  }
  if (!Number.isInteger(dash.charges) || dash.charges < 1 || !(dash.recharge > 0)) {
    throw new Error(`Character ${id} dash needs at least one charge and a positive recharge.`);
  }
}

function validatePortrait({ id, spriteSheet }: CharacterDefinition): void {
  const portrait = spriteSheet?.portrait;
  if (!spriteSheet || !portrait) return;
  if (
    !(portrait.size > 0) ||
    portrait.x < 0 ||
    portrait.y < 0 ||
    portrait.x + portrait.size > spriteSheet.frameWidth ||
    portrait.y + portrait.size > spriteSheet.frameHeight
  ) {
    throw new Error(`Character ${id} portrait must be a square inside its sprite frame.`);
  }
}

function validateDifficulty(difficulty: DifficultyDefinition): void {
  if (difficulty.maxWaveEnemies < 1) {
    throw new Error("difficulty.maxWaveEnemies must be at least one.");
  }
  if (difficulty.soloEnemyHealth <= 0 || difficulty.soloBossHealth <= 0) {
    throw new Error("difficulty solo health multipliers must be greater than zero.");
  }
  if (
    difficulty.enemyHealthPerExtraPlayer < 0 ||
    difficulty.bossHealthPerExtraPlayer < 0 ||
    difficulty.largePartyBossHealthPerExtraPlayer < 0
  ) {
    throw new Error("difficulty per-player health multipliers cannot be negative.");
  }
  if (difficulty.enemyDamageBonusAtPlayers < 1) {
    throw new Error("difficulty.enemyDamageBonusAtPlayers must be at least one.");
  }
  if (difficulty.enemyDamageBonus < 0) {
    throw new Error("difficulty.enemyDamageBonus cannot be negative.");
  }
  for (const tier of DIFFICULTY_TIERS) {
    const profile = difficulty.tiers?.[tier];
    if (!profile) throw new Error(`difficulty.tiers.${tier} is required.`);
    if (profile.enemyHealth <= 0 || profile.bossHealth <= 0 || profile.speed <= 0 || profile.cooldown <= 0) {
      throw new Error(`difficulty.tiers.${tier} multipliers must be greater than zero.`);
    }
    if (profile.damageBonus < 0) {
      throw new Error(`difficulty.tiers.${tier} damage bonus cannot be negative.`);
    }
    if (profile.coffeeDuration <= 0 || !Number.isInteger(profile.coffeeBags) || profile.coffeeBags < 0) {
      throw new Error(`difficulty.tiers.${tier} coffee duration and bag count are invalid.`);
    }
    if (profile.coffeeRegen < 0) {
      throw new Error(`difficulty.tiers.${tier} coffee regen cannot be negative.`);
    }
    if (!Number.isInteger(profile.lives) || profile.lives < 1) {
      throw new Error(`difficulty.tiers.${tier} lives must be a positive integer.`);
    }
    if (profile.specialFraction <= 0 || profile.specialFraction > 1) {
      throw new Error(`difficulty.tiers.${tier} special fraction must be in (0, 1].`);
    }
    if (!Number.isInteger(profile.specialExecuteHealth) || profile.specialExecuteHealth < 0) {
      throw new Error(`difficulty.tiers.${tier} special execute health must be a non-negative integer.`);
    }
  }
  const medium = difficulty.tiers.medium;
  if (
    medium.enemyHealth !== 1 ||
    medium.bossHealth !== 1 ||
    medium.damageBonus !== 0 ||
    medium.speed !== 1 ||
    medium.cooldown !== 1
  ) {
    throw new Error("difficulty.tiers.medium must leave the player-count curve unchanged.");
  }
}

export const CONTENT = validateContent({
  characters,
  enemies,
  levels,
  tuning
});

export const CHARACTER_BY_ID = new Map(characters.map((character) => [character.id, character]));
export const ENEMY_BY_ID = new Map(enemies.map((enemy) => [enemy.id, enemy]));
